import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import translate from "@insite/client-framework/Translate";
import {
    CartLineModel,
    ProductSettingsModel,
    ProductSubscriptionDto,
    PromotionModel,
} from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { FutureAvailabilityTooltip } from "@waytek/Components/FutureAvailabilityTooltip";
import getProductCategories from "@waytek/Handlers/Utilities/CustomGetProductCategories";
import { trackRemoveFromCartEvent } from "@waytek/Handlers/Utilities/CustomTracking";
import Tooltip, { TooltipPresentationProps } from "@waytek/MobiusOverrides/Tooltip/Tooltip";
import CartLineNotes, { CartLineNotesStyles } from "@waytek/Overrides/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@waytek/Overrides/Widgets/Cart/CartLineQuantity";
import updateCartLine from "@waytek/Store/Pages/Cart/Handlers/UpdateCartLineApiResult";
import { fontFamilyAlt, mediaQueriesBP } from "@waytek/Utilities/variables";
import { CustomerNumberComponentStyles } from "@waytek/Widgets/Custom/CustomerNumberComponent";
import ScheduleShipmentDetailComponent, {
    ScheduleShipmentDetailStyles,
} from "@waytek/Widgets/Custom/ScheduleShipmentDetailComponent";
import ScheduleShipmentRenderComponent from "@waytek/Widgets/Custom/ScheduleShipmentRenderComponent";
import debounce from "lodash/debounce";
import React, { FC, useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    handleScheduleShipmentOpen: (cartLine: CartLineModel) => void;
    extendedStyles?: CartLineCardCondensedStyles;
    bucketType?: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const {
        wishListSettings,
        customSettings,
        productSettings: { enableVat, vatPriceDisplay },
    } = getSettingsCollection(state);
    return {
        wishListSettings,
        enableVat,
        vatPriceDisplay,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        location: getLocation(state),
        isGuest: state.context.session?.isGuest || !state.context.session.isAuthenticated,
        customSettings: customSettings as any,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    getProductCategories,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardCondensedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotesGridItemMobile?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    removeIconColumn?: GridItemProps;
    subtotalColumn?: GridItemProps;
    quantityWrapper?: InjectableCss;
    subtotalWrapper?: InjectableCss;
    addToListWrapper?: InjectableCss;
    productNumbersMobile?: InjectableCss;
    removeIconColumnMobile?: InjectableCss;
    productMinQtyStockItem?: InjectableCss;
    productMinQtyStockLabel?: TypographyPresentationProps;
    productMinQtyStockValue?: TypographyPresentationProps;
    productStockWrapper?: InjectableCss;
    subtotalLabel?: TypographyPresentationProps;
    customerLabel?: InjectableCss;
    customerWrapper?: InjectableCss;
    customerWrapperMobile?: InjectableCss;
    modalImageGridItem?: GridItemProps;
    modalProductDescriptionGridItem?: GridItemProps;
    modalQuantityGridItem?: GridItemProps;
    modalTopSection?: InjectableCss;
    modalBottomSection?: InjectableCss;
    quantityWrapperMobile?: InjectableCss;
    modalPriceWrapper?: InjectableCss;
    modalQuantityWrapper?: InjectableCss;
    modalSectionTitle?: TypographyPresentationProps;
    modalBackButton?: ButtonPresentationProps;
    modalContinueButton?: ButtonPresentationProps;
    modalBottomText?: GridItemProps;
    modalBottomControls?: GridItemProps;
    schedulesShipmentLinkWrapper?: InjectableCss;
    schedulesShipmentLink?: LinkPresentationProps;
    tooltip?: TooltipPresentationProps;
    scheduleShipmentLinkMobile?: InjectableCss;
    customerNumber?: CustomerNumberComponentStyles;
    scheduleShipmentDetail?: ScheduleShipmentDetailStyles;
    scheduleShipmentDetailMobile?: InjectableCss;
    availabilityLabelText?: TypographyPresentationProps;
    tooltipWrapper?: TooltipPresentationProps;
    tooltipLink?: LinkPresentationProps;
    tooltipTypography?: TypographyPresentationProps;
}

// eslint-disable-next-line
/* eslint-disable sonarjs/no-nested-template-literals */
export const cartLineCardExpandedStyles: CartLineCardCondensedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: { width: [12, 12, 12, 7, 7] },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: { width: 12 },
    productPartNumbers: {
        customerNameLabelText: {
            css: css`
                display: none;
            `,
        },
        customerNameValueText: {
            css: css`
                display: none;
            `,
        },
    },
    quantityAndExtendedUnitNetPriceGridItem: { width: 12 },
    quantityGridItem: { width: 6 },
    extendedUnitNetPriceGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    addToListLink: {
        css: css`
            margin-top: 20px;
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: { width: 12 },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },

    scheduleShipmentDetailMobile: {
        css: css`
            order: 6;
            ${mediaQueriesBP.minMD} {
                display: none;
                order: unset;
            }
        `,
    },
    availabilityLabelText: {
        weight: 600,
        size: "14px",
        lineHeight: "24px",
    },
    tooltipWrapper: {
        cssOverrides: {
            tooltipClickable: css`
                &&& {
                    margin-left: 5px;
                    padding: 0;
                }
            `,
        },
    },
    tooltipLink: {
        typographyProps: {
            size: "14px",
            lineHeight: "24px",
        },
    },
    tooltipTypography: {
        size: "14px",
        fontFamily: fontFamilyAlt,
        css: css`
            text-align: left;
            &&& {
                font-size: 14px;
            }
        `,
    },
};
// eslint-disable-next-line
/* eslint-enable sonarjs/no-nested-template-literals */

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    handleScheduleShipmentOpen,
    signInUrl,
    location,
    isGuest,
    bucketType,
    customSettings,
    getProductCategories,
    // eslint-disable-next-line
}) => {
    const [isMobile, setIsMobile] = React.useState(false);

    React.useEffect(() => {
        const handleWindowSize = () => {
            if (window.innerWidth <= 1024) {
                setIsMobile(true);
            } else {
                setIsMobile(false);
            }
        };
        handleWindowSize();
        window.addEventListener("resize", debounce(handleWindowSize, 200));
    }, []);

    const modalOpenHandler = () => {
        if (!isGuest) {
            handleScheduleShipmentOpen(cartLine);
        }
    };

    const toasterContext = useContext(ToasterContext);
    // eslint-disable-next-line
    /* eslint-disable sonarjs/cognitive-complexity */
    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                const cartLineUpdated: CartLineModel = { ...cartLine, properties: {} };
                for (const key in cartLine.properties) {
                    if (key !== "multipleSaleQty" && key !== "roundingRule") {
                        cartLineUpdated.properties[key] = cartLine.properties[key];
                    }
                }

                updateCartLine({
                    cartLine: {
                        ...cartLineUpdated,
                        qtyOrdered,
                    },
                    onSuccess: apiResult => {
                        if (apiResult?.isQtyAdjusted) {
                            toasterContext.addToast({
                                body: <Typography variant="p">{siteMessage("Cart_QuantityAdjusted")}</Typography>,
                                messageType: "warning",
                                timeoutLength: 1000 * 10,
                            });
                        }
                    },
                    onComplete(resultProps) {
                        if (resultProps.apiResult) {
                            // "this" is targeting the object being created, not the parent SFC
                            // eslint-disable-next-line react/no-this-in-sfc
                            this.onSuccess?.(resultProps.apiResult);
                        }
                    },
                });
            }
        }
    };
    // eslint-disable-next-line
    /* eslint-enable sonarjs/cognitive-complexity */

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes: notes.slice(0, customSettings.notesMaxNumberOfCharacters).trim(),
                },
                shouldNotReloadPromotions: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({
            cartLineId: cartLine.id,
            /* Verndale Custom Feature - START */
            onComplete: () => {
                if (cartLine.productId) {
                    getProductCategories({
                        ProductIds: [cartLine.productId],
                        onComplete: result => {
                            if (result && result.apiResult) {
                                trackRemoveFromCartEvent([cartLine], result.apiResult.productProperties);
                            }
                        },
                    });
                }
            },
            /* Verndale Custom Feature - END */
        });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
            notes: cartLine.notes,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const removeScheduleHandler = () => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    ...cartLine.properties,
                    scheduledShipments: "",
                },
            },
        });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    const isWired = cartLine.properties["isWired"] && cartLine.properties["isWired"].toLowerCase() === "true";

    const {
        qtyOnHand,
        qtyOrdered,
        properties: { futureAvailability, qtyOnOrder },
    } = cartLine;
    const showTooltip =
        qtyOnHand > 0 || (qtyOrdered && qtyOrdered > 0) || (futureAvailability && futureAvailability.length > 0);

    const isPartialShipment =
        cartLine.properties["isPartialShipment"] && cartLine.properties["isPartialShipment"].toLowerCase() === "true";
    const isBackordered =
        cartLine.properties["isBackordered"] && cartLine.properties["isBackordered"].toLowerCase() === "true";

    const hasScheduledShipments = cartLine.properties["scheduledShipments"]?.trim().length > 0;

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </GridItem>
            <GridItem {...styles.cartLineInfoGridItem}>
                <GridContainer {...styles.cartLineInfoContainer}>
                    <GridItem {...styles.productInfoGridItem}>
                        <GridContainer {...styles.productInfoContainer}>
                            <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                {cartLine.brand && (
                                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                )}
                                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                                <StyledWrapper {...styles.removeIconColumnMobile}>
                                    {showRemoveAction && (
                                        <Clickable
                                            onClick={removeCartLineClickHandler}
                                            data-test-selector="cartline_removeLine"
                                        >
                                            <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                                            <IconMemo {...styles.removeCartLineIcon} />
                                        </Clickable>
                                    )}
                                </StyledWrapper>
                            </GridItem>
                            {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                <GridItem {...styles.configurationGridItem}>
                                    {cartLine.sectionOptions!.map(option => (
                                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                            {`${option.sectionName}:${option.optionName}`}
                                        </Typography>
                                    ))}
                                </GridItem>
                            )}
                            <GridItem {...styles.productPartNumbersGridItem}>
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    extendedStyles={styles.productPartNumbers}
                                />

                                {(isOutOfStock(cartLine) || isBackordered || isPartialShipment) &&
                                    !hasScheduledShipments &&
                                    showTooltip && (
                                        <StyledWrapper {...styles.schedulesShipmentLinkWrapper}>
                                            <Typography {...styles.availabilityLabelText}>
                                                {translate("Availabilty")}
                                            </Typography>
                                            :{" "}
                                            <Tooltip
                                                {...styles.tooltipWrapper}
                                                triggerComponent={
                                                    <Link {...styles.tooltipLink}>{translate("More")}</Link>
                                                }
                                                text="..."
                                                content={
                                                    <FutureAvailabilityTooltip
                                                        futureAvailability={JSON.parse(futureAvailability ?? "[]")}
                                                        qtyOnHand={qtyOnHand}
                                                        unitOfMeasure={cartLine.unitOfMeasure}
                                                        qtyOnOrder={qtyOnOrder}
                                                    />
                                                }
                                                typographyProps={styles.tooltipTypography}
                                                triggerOnHover={!isMobile}
                                            />
                                        </StyledWrapper>
                                    )}

                                <StyledWrapper {...styles.schedulesShipmentLinkWrapper}>
                                    <ScheduleShipmentRenderComponent
                                        modalOpenHandler={modalOpenHandler}
                                        minOrderSubtotal={customSettings.orderMinimumToScheduleShip}
                                        styles={styles}
                                        hasShipments={cartLine.properties["scheduledShipments"]?.trim().length > 0}
                                        removeShipments={removeScheduleHandler}
                                    />
                                </StyledWrapper>
                                {cartLine.properties["scheduledShipments"] &&
                                    cartLine.properties["scheduledShipments"].length > 0 && (
                                        <ScheduleShipmentDetailComponent
                                            cartLineId={cartLine.id}
                                            scheduledShipments={JSON.parse(cartLine.properties["scheduledShipments"])}
                                            extendedStyles={styles.scheduleShipmentDetail}
                                            variant="condensed"
                                        />
                                    )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>

                    {cart.showCostCode && !cartLine.isPromotionItem && (
                        <GridItem {...styles.costCodeGridItem}>
                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                            {cart.canEditCostCode ? (
                                <Select
                                    value={cartLine.costCode}
                                    onChange={costCodeChangeHandler}
                                    {...styles.costCodeSelect}
                                >
                                    {cart.costCodes?.map(costCode => (
                                        <option key={costCode.costCode} value={costCode.costCode}>
                                            {costCode.description}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                            )}
                        </GridItem>
                    )}

                    {showLineNotes && isWired && (
                        <GridItem {...styles.cartLineNotesGridItem}>
                            <CartLineNotes
                                cart={cart}
                                editable={true}
                                onNotesChange={notesChangeHandler}
                                extendedStyles={styles.cartLineNotes}
                                defaultNotes={cartLine.notes}
                            />
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
            <GridItem {...styles.removeCartLineGridItem}>
                <GridContainer>
                    <GridItem {...styles.subtotalColumn}>
                        <GridContainer>
                            <GridItem width={12} {...styles.productPriceAndAvailabilityGridItem}>
                                <StyledWrapper {...styles.productNumbersMobile}>
                                    <ProductPartNumbers
                                        productNumber={cartLine.erpNumber}
                                        customerProductNumber={cartLine.customerName}
                                        extendedStyles={styles.productPartNumbers}
                                    />
                                </StyledWrapper>
                                {!cart.cartNotPriced && (
                                    <ProductPrice
                                        product={cartLine}
                                        currencySymbol={cart.currencySymbol}
                                        showSavings={true}
                                        showSavingsAmount={productSettings.showSavingsAmount}
                                        showSavingsPercent={productSettings.showSavingsPercent}
                                        showInvalidPriceMessage={true}
                                        extendedStyles={styles.productPrice}
                                    />
                                )}
                                {promotions.map(promotion => (
                                    <Typography {...styles.promotionNameText} key={promotion.id}>
                                        {promotion.name}
                                    </Typography>
                                ))}
                                {/* {showInventoryAvailability && !cartLine.quoteRequired && (
                                        <ProductAvailability
                                            productId={cartLine.productId!}
                                            availability={cartLine.availability!}
                                            unitOfMeasure={cartLine.unitOfMeasure}
                                            trackInventory={cartLine.trackInventory}
                                            failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                            extendedStyles={styles.productAvailability}
                                        />
                                )} */}
                                {cartLine.isSubscription && cartLine.productSubscription && (
                                    <ProductDeliveryScheduleButton
                                        subscription={
                                            cartLine.properties.productSubscription
                                                ? JSON.parse(cartLine.properties.productSubscription)
                                                : cartLine.productSubscription
                                        }
                                        disabled={
                                            !cart.canModifyOrder ||
                                            cartLine.isPromotionItem ||
                                            !cartLine.qtyOrdered ||
                                            cartLine.qtyOrdered <= 0
                                        }
                                        onSave={saveScheduleHandler}
                                        extendedStyles={styles.deliveryScheduleButton}
                                    />
                                )}

                                <StyledWrapper {...styles.quantityWrapper}>
                                    <CartLineQuantity
                                        cart={cart}
                                        editable={true}
                                        onQtyOrderedChange={qtyOrderedChangeHandler}
                                        extendedStyles={styles.quantity}
                                        bucketType={bucketType}
                                    />
                                    {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && (
                                        <StyledWrapper {...styles.subtotalWrapper}>
                                            <Typography {...styles.subtotalLabel} as="p">
                                                {translate("Subtotal:")}&nbsp;
                                            </Typography>
                                            <Typography
                                                {...styles.extendedUnitNetPriceText}
                                                data-test-selector="cartline_extendedUnitNetPrice"
                                            >
                                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                    : cartLine.pricing.extendedUnitNetPriceDisplay}
                                            </Typography>
                                            {enableVat && (
                                                <>
                                                    <Typography as="p" {...styles.vatLabelText}>
                                                        {vatPriceDisplay === "DisplayWithVat" ||
                                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                            ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                                            : translate("Ex. VAT")}
                                                    </Typography>
                                                    {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                        <>
                                                            <Typography {...styles.subtotalWithoutVatText}>
                                                                {cartLine.pricing.extendedUnitNetPriceDisplay}
                                                            </Typography>
                                                            <Typography as="p" {...styles.vatLabelText}>
                                                                {translate("Ex. VAT")}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>

                                <StyledWrapper {...styles.addToListWrapper}>
                                    <StyledWrapper {...styles.scheduleShipmentLinkMobile}>
                                        <ScheduleShipmentRenderComponent
                                            modalOpenHandler={modalOpenHandler}
                                            minOrderSubtotal={customSettings.orderMinimumToScheduleShip}
                                            styles={styles}
                                            hasShipments={cartLine.properties["scheduledShipments"]?.trim().length > 0}
                                            removeShipments={removeScheduleHandler}
                                        />
                                    </StyledWrapper>

                                    {!hideAddToList && cartLine.canAddToWishlist && (
                                        <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                                            {translate("Add to List")}
                                        </Link>
                                    )}
                                </StyledWrapper>

                                {cartLine.properties["scheduledShipments"] &&
                                    cartLine.properties["scheduledShipments"].length > 0 && (
                                        <StyledWrapper {...styles.scheduleShipmentDetailMobile}>
                                            <ScheduleShipmentDetailComponent
                                                cartLineId={cartLine.id}
                                                scheduledShipments={JSON.parse(
                                                    cartLine.properties["scheduledShipments"],
                                                )}
                                                extendedStyles={styles.scheduleShipmentDetail}
                                                variant="condensed"
                                            />
                                        </StyledWrapper>
                                    )}

                                {errorMessages.length > 0 && (
                                    <StyledWrapper>
                                        {errorMessages.map((message, index) => (
                                            <Typography
                                                {...styles.cartLineErrorMessageText}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={index}
                                                data-test-selector="cartline_errorMessage"
                                            >
                                                {message}
                                            </Typography>
                                        ))}
                                    </StyledWrapper>
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>

                    {showLineNotes && isWired && (
                        <GridItem {...styles.cartLineNotesGridItemMobile}>
                            <CartLineNotes
                                cart={cart}
                                editable={true}
                                onNotesChange={notesChangeHandler}
                                extendedStyles={styles.cartLineNotes}
                                defaultNotes={cartLine.notes}
                            />
                        </GridItem>
                    )}

                    <GridItem {...styles.removeIconColumn}>
                        {showRemoveAction && (
                            <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                                <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                                <IconMemo {...styles.removeCartLineIcon} />
                            </Clickable>
                        )}
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
